/**
 * Wall Geometry Utilities
 *
 * Pure geometry functions for wall corner calculations, surface detection,
 * and room wall re-wrapping. No Three.js dependency — plain {x,y,z} objects.
 *
 * Module format: CommonJS
 */

const { applyRotation3D } = require('./rotationUtils');
const {
  DEFAULT_WALL_THICKNESS,
  ZERO_THICKNESS_THRESHOLD,
} = require('./wallConstants');

// --- Constants ---

const EPSILON = 0.05; // 5cm minimum dimension threshold
const THIN_THRESHOLD = ZERO_THICKNESS_THRESHOLD; // Re-export alias for backward compat
const DEFAULT_THICKNESS = DEFAULT_WALL_THICKNESS; // Re-export alias for backward compat
const PARALLEL_THRESHOLD = 1e-9;
const ALIGNMENT_ANGLE_TOLERANCE = 0.0698; // 4° in radians — wall axis-alignment tolerance shared by wall merging and stretch math

// --- Core rotation helper ---

/**
 * Apply Euler rotation (X → Y → Z) to a direction vector.
 * Delegates to rotationUtils.applyRotation3D but accepts individual angles.
 *
 * @param {number} rx - X rotation in radians
 * @param {number} ry - Y rotation in radians
 * @param {number} rz - Z rotation in radians
 * @param {number} x - Initial X component
 * @param {number} y - Initial Y component
 * @param {number} z - Initial Z component
 * @returns {Object} Rotated direction {x, y, z}
 */
function applyRotation(rx, ry, rz, x, y, z) {
  return applyRotation3D({ x: rx, y: ry, z: rz }, [x, y, z]);
}

// --- Line intersection (XZ plane) ---

/**
 * Intersect two lines defined by two points each, projected onto the XZ plane.
 * Returns the intersection point as {x, y, z} with Y averaged from the two
 * line starts, or null if the lines are parallel (|det| < PARALLEL_THRESHOLD).
 *
 * Same semantics as functions/shared/geometryUtils.js#findLineIntersection;
 * lives here so shared-lib code can intersect lines without inverting the
 * shared-lib → functions dependency direction.
 *
 * @param {Object} line1Start - {x, y, z}
 * @param {Object} line1End - {x, y, z}
 * @param {Object} line2Start - {x, y, z}
 * @param {Object} line2End - {x, y, z}
 * @returns {Object|null}
 */
function intersectLinesXZ(line1Start, line1End, line2Start, line2End) {
  const x1 = line1Start.x;
  const z1 = line1Start.z;
  const x2 = line1End.x;
  const z2 = line1End.z;
  const x3 = line2Start.x;
  const z3 = line2Start.z;
  const x4 = line2End.x;
  const z4 = line2End.z;

  const dx1 = x2 - x1;
  const dz1 = z2 - z1;
  const dx2 = x4 - x3;
  const dz2 = z4 - z3;

  const det = dx1 * dz2 - dz1 * dx2;
  if (Math.abs(det) < PARALLEL_THRESHOLD) return null;

  const t = ((x3 - x1) * dz2 - (z3 - z1) * dx2) / det;
  return {
    x: x1 + t * dx1,
    y: (line1Start.y + line2Start.y) / 2,
    z: z1 + t * dz1,
  };
}

// --- Wall direction / normal ---

/**
 * Get wall direction vector — uses alignment corners if available, otherwise rotation.
 * @param {Object} wall - Wall with rotation and/or alignment
 * @returns {Object} Direction vector {x, y, z}
 */
function getWallDirection(wall) {
  if (
    wall.alignment &&
    wall.alignment.bottomLeft &&
    wall.alignment.bottomRight
  ) {
    return calculateDirectionFromPoints(
      wall.alignment.bottomLeft,
      wall.alignment.bottomRight
    );
  }
  const rx = wall.rotation?.x || 0;
  const ry = wall.rotation?.y || 0;
  const rz = wall.rotation?.z || 0;
  return applyRotation(rx, ry, rz, 1, 0, 0);
}

/**
 * Get wall normal vector (perpendicular to wall face).
 * @param {Object} wall - Wall with rotation
 * @returns {Object} Normal vector {x, y, z}
 */
function getWallNormal(wall) {
  const rx = wall.rotation?.x || 0;
  const ry = wall.rotation?.y || 0;
  const rz = wall.rotation?.z || 0;
  return applyRotation(rx, ry, rz, 0, 0, 1);
}

// --- Corner calculations ---

/**
 * Calculate wall bottom corner points from center, dimensions, and rotation.
 * @param {Object} wall - Wall with center, dimensions, rotation
 * @returns {Object|null} {bottomLeft, bottomRight, bottomMiddle} or null if degenerate
 */
function calculateWallCorners(wall) {
  if (
    !wall.dimensions ||
    wall.dimensions.x < EPSILON ||
    wall.dimensions.y < EPSILON
  ) {
    return null;
  }

  const center = wall.center;
  const width = wall.dimensions.x;
  const height = wall.dimensions.y;
  const rightVector = getWallDirection(wall);
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  const bottomLeft = {
    x: center.x - halfWidth * rightVector.x,
    y: center.y - halfHeight,
    z: center.z - halfWidth * rightVector.z,
  };

  const bottomRight = {
    x: center.x + halfWidth * rightVector.x,
    y: center.y - halfHeight,
    z: center.z + halfWidth * rightVector.z,
  };

  const bottomMiddle = {
    x: (bottomLeft.x + bottomRight.x) / 2,
    y: (bottomLeft.y + bottomRight.y) / 2,
    z: (bottomLeft.z + bottomRight.z) / 2,
  };

  return { bottomLeft, bottomRight, bottomMiddle };
}

// --- Direction helpers ---

/**
 * Normalized direction from point p1 to point p2.
 * @param {Object} p1 - Start {x, y, z}
 * @param {Object} p2 - End {x, y, z}
 * @returns {Object} Normalized direction {x, y, z}
 */
function calculateDirectionFromPoints(p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dz = p2.z - p1.z;
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (length < 0.001) {
    return { x: 1, y: 0, z: 0 };
  }
  return { x: dx / length, y: dy / length, z: dz / length };
}

/**
 * Wall direction from its corner points.
 * @param {Object} corners - {bottomLeft, bottomRight}
 * @returns {Object} Normalized direction {x, y, z}
 */
function calculateWallDirection(corners) {
  return calculateDirectionFromPoints(corners.bottomLeft, corners.bottomRight);
}

// --- Collinear coverage ---

/**
 * Two centerlines count as the same physical wall LINE when their
 * directions align (allowing a 180° flip) and one lies within this
 * perpendicular distance of the other. Same values as
 * `split-level/reconstructDoubleHeight`'s local pair — that module should
 * adopt these when its detection core is extracted (see
 * claude-docs/2026-08-02-open-loft-second-story-representation.md §Phase 3),
 * which is why they live here rather than being duplicated a third time.
 *
 * The 0.3 m perpendicular tolerance is measured, not guessed: on the
 * 66616D52 two-story capture the largest offset between a wall and its
 * genuine counterpart one story up is 0.226 m, while the nearest
 * NON-counterpart is 0.662 m away.
 */
const COLLINEAR_PARALLEL_DOT_MIN = 0.98;
const COLLINEAR_PERP_TOL_M = 0.3;

/**
 * Fraction of `wall`'s centerline covered by the UNION of the collinear
 * segments among `others`.
 *
 * Union, not max-per-segment: a story often splits one physical wall line
 * into several walls that abut rather than overlap, and asking "does the
 * other story have its own wall here?" must see those as one covered span.
 * (On 66616D52 the loft's south line is two abutting upstairs walls at
 * 53 % and 47 % — separately below any sane threshold, together 100 %.)
 *
 * @param {Object} wall - wall whose span is being measured
 * @param {Object[]} others - candidate covering walls
 * @param {Object} [opts]
 * @param {number} [opts.parallelDotMin] - direction alignment threshold
 * @param {number} [opts.perpTolM] - max perpendicular offset, metres
 * @returns {number} 0…1; 0 when `wall` has no usable centerline
 */
function collinearUnionCoverage(wall, others, opts = {}) {
  const {
    parallelDotMin = COLLINEAR_PARALLEL_DOT_MIN,
    perpTolM = COLLINEAR_PERP_TOL_M,
  } = opts;

  const corners = calculateWallCorners(wall);
  if (!corners) return 0;
  const a = corners.bottomLeft;
  const axis = calculateWallDirection(corners);
  const len = Math.hypot(
    corners.bottomRight.x - a.x,
    corners.bottomRight.z - a.z
  );
  if (len < 1e-6) return 0;

  const along = (p) => (p.x - a.x) * axis.x + (p.z - a.z) * axis.z;
  const perpOff = (p) => {
    const t = along(p);
    return Math.hypot(p.x - a.x - t * axis.x, p.z - a.z - t * axis.z);
  };

  const intervals = [];
  for (const other of others) {
    const oc = calculateWallCorners(other);
    if (!oc) continue;
    const oAxis = calculateWallDirection(oc);
    if (Math.abs(axis.x * oAxis.x + axis.z * oAxis.z) < parallelDotMin)
      continue;
    if (perpOff(oc.bottomLeft) > perpTolM) continue;
    if (perpOff(oc.bottomRight) > perpTolM) continue;
    const t0 = Math.max(
      0,
      Math.min(along(oc.bottomLeft), along(oc.bottomRight))
    );
    const t1 = Math.min(
      len,
      Math.max(along(oc.bottomLeft), along(oc.bottomRight))
    );
    if (t1 > t0) intervals.push([t0, t1]);
  }

  intervals.sort((p, q) => p[0] - q[0]);
  let covered = 0;
  let cur = null;
  for (const [start, end] of intervals) {
    if (!cur) {
      cur = [start, end];
    } else if (start <= cur[1]) {
      cur[1] = Math.max(cur[1], end);
    } else {
      covered += cur[1] - cur[0];
      cur = [start, end];
    }
  }
  if (cur) covered += cur[1] - cur[0];
  return covered / len;
}

// --- Face / surface detection ---

/**
 * Determines if two wall segments face the same direction (dot product > 0).
 * @param {Object} structureCorners - {bottomLeft, bottomRight}
 * @param {Object} roomCorners - {bottomLeft, bottomRight}
 * @returns {boolean}
 */
function calculateFacesSameDirection(structureCorners, roomCorners) {
  const structureDir = calculateWallDirection(structureCorners);
  const roomDir = calculateWallDirection(roomCorners);
  return (
    structureDir.x * roomDir.x +
      structureDir.y * roomDir.y +
      structureDir.z * roomDir.z >
    0
  );
}

/**
 * Whether an input (RoomPlan) wall was captured from inside the room
 * being built around `roomWallAlignment`. Returns `true` iff the input
 * wall's BL→BR direction agrees with the room wall's — which, by the
 * BL/BR convention documented in
 * `functions/features/room-polygon-build/wall-construction.js:393-402`
 * and verified empirically in
 * `~/.claude/.../memory/project_blbr_convention.md`, means the input
 * wall's normal points INTO this room. Returns `null` when alignment
 * data is missing so callers can distinguish "not captured here" from
 * "can't tell".
 */
function inputCapturedFromThisRoom(inputWall, roomWallAlignment) {
  const inputCorners = calculateWallCorners(inputWall);
  if (!inputCorners) return null;
  if (!roomWallAlignment?.bottomLeft || !roomWallAlignment?.bottomRight) {
    return null;
  }
  return calculateFacesSameDirection(inputCorners, roomWallAlignment);
}

/**
 * Normal × halfThickness offset for a structure wall surface.
 * @param {Object} structureWall - Wall with dimensions and rotation
 * @param {boolean} facesSameDirection
 * @returns {Object} Offset {x, y, z}
 */
function calculateInteriorOffset(structureWall, facesSameDirection) {
  const wallThickness = structureWall.dimensions?.z || DEFAULT_THICKNESS;
  const normal = getWallNormal(structureWall);
  const offsetMagnitude = wallThickness * 0.5;
  const offsetSign = facesSameDirection ? 1 : -1;
  return {
    x: normal.x * offsetMagnitude * offsetSign,
    y: 0,
    z: normal.z * offsetMagnitude * offsetSign,
  };
}

/**
 * Structure wall face corners (offset by half-thickness along normal).
 * @param {Object} structureWall
 * @param {boolean} facesSameDirection - Which face to use
 * @returns {Object|null} {bottomLeft, bottomRight}
 */
function calculateSurfaceCorners(structureWall, facesSameDirection) {
  const structureCorners = calculateWallCorners(structureWall);
  if (!structureCorners) return null;

  const wallThickness = structureWall.dimensions?.z || 0;
  if (wallThickness < THIN_THRESHOLD) {
    return structureCorners;
  }

  const offset = calculateInteriorOffset(structureWall, facesSameDirection);
  return {
    bottomLeft: {
      x: structureCorners.bottomLeft.x + offset.x,
      y: structureCorners.bottomLeft.y,
      z: structureCorners.bottomLeft.z + offset.z,
    },
    bottomRight: {
      x: structureCorners.bottomRight.x + offset.x,
      y: structureCorners.bottomRight.y,
      z: structureCorners.bottomRight.z + offset.z,
    },
  };
}

/**
 * Full surface-corner calculation with automatic face detection.
 * @param {Object} structureWall
 * @param {Object} roomWall - Must have alignment with bottomLeft/bottomRight
 * @returns {Object|null} {bottomLeft, bottomRight, facesSameDirection}
 */
function findInteriorSurfaceCorners(structureWall, roomWall) {
  if (!roomWall.alignment) return null;

  const structureCorners = calculateWallCorners(structureWall);
  if (!structureCorners) return null;

  const facesSameDirection = calculateFacesSameDirection(
    structureCorners,
    roomWall.alignment
  );

  const wallThickness = structureWall.dimensions?.z || 0;
  if (wallThickness < THIN_THRESHOLD) {
    return {
      bottomLeft: structureCorners.bottomLeft,
      bottomRight: structureCorners.bottomRight,
      facesSameDirection,
    };
  }

  const surface = calculateSurfaceCorners(structureWall, facesSameDirection);
  return { ...surface, facesSameDirection };
}

// --- Plan-view footprint (mitered) ---

/**
 * Plan-view footprint of a wall, honoring `leftMiter` / `rightMiter`.
 *
 * Returns the four bottom corners of the wall in world frame as
 * `{ frontLeft, frontRight, backRight, backLeft }`. With both miters
 * zero, the result equals today's rectangular footprint (centerline
 * length = `dimensions.x`, surfaces offset by half-thickness along the
 * wall normal).
 *
 * Sign convention for miters (radians, defaulting to 0):
 *   - `leftMiter > 0`  → at the left end (-X), the back-face corner is
 *                        further "out" (more negative X) than the
 *                        front-face corner. Equivalently: back face is
 *                        longer than the front face on the left side.
 *   - `rightMiter > 0` → same on the right end (+X).
 *   - Negative miters → front face longer at that end (reflex corner).
 *
 * `facesSameDirection` mirrors the existing surface-corner API: when
 * true, the room-interior face is on the +normal (local +Z) side; when
 * false, on the -normal side. Defaults to true.
 *
 * @param {Object} wall - Wall with center, dimensions, rotation,
 *                         optionally leftMiter / rightMiter.
 * @param {boolean} [facesSameDirection=true]
 * @returns {Object|null} { frontLeft, frontRight, backRight, backLeft }
 *                         each {x, y, z} at wall base Y; null if wall
 *                         dimensions are degenerate.
 */
function calculateWallFootprint(wall, facesSameDirection = true) {
  if (
    !wall.dimensions ||
    wall.dimensions.x < EPSILON ||
    wall.dimensions.y < EPSILON
  ) {
    return null;
  }

  const W = wall.dimensions.x;
  const t = wall.dimensions.z || 0;
  const halfH = wall.dimensions.y / 2;
  const leftMiter = wall.leftMiter || 0;
  const rightMiter = wall.rightMiter || 0;
  const frontSign = facesSameDirection ? +1 : -1;
  const halfT = t / 2;

  const localCorners = {
    frontLeft: [-W / 2, -halfH, +frontSign * halfT],
    frontRight: [+W / 2, -halfH, +frontSign * halfT],
    backRight: [+W / 2 + t * Math.tan(rightMiter), -halfH, -frontSign * halfT],
    backLeft: [-W / 2 - t * Math.tan(leftMiter), -halfH, -frontSign * halfT],
  };

  const out = {};
  for (const [name, [lx, ly, lz]] of Object.entries(localCorners)) {
    const r = applyRotation3D(wall.rotation || { x: 0, y: 0, z: 0 }, [
      lx,
      ly,
      lz,
    ]);
    out[name] = {
      x: wall.center.x + r.x,
      y: wall.center.y + r.y,
      z: wall.center.z + r.z,
    };
  }
  return out;
}

// --- Room wall geometry construction ---

/**
 * Build room wall geometry from an edge of a CCW-from-above room polygon.
 *
 * Convention: the polygon is traversed counterclockwise when viewed from
 * above (interior on the left of travel). For each edge, traversal goes
 * from the wall's right corner to its left corner viewed from inside the
 * room. So:
 *   edgeStart = traversal start = right corner = bottomRight
 *   edgeEnd   = traversal end   = left corner  = bottomLeft
 *
 * Rotation is computed so the wall normal points INTO the room.
 *
 * Returns a partial wall object ({center, dimensions, rotation,
 * polygonCorners, alignment}) — the caller adds id, category,
 * structureWallId, etc.
 *
 * @param {{x, z}} edgeStart - Edge start (becomes BR). Y is ignored.
 * @param {{x, z}} edgeEnd   - Edge end (becomes BL). Y is ignored.
 * @param {number} wallHeight - dimensions.y
 * @param {number} floorY     - Y of the floor plane
 * @returns {Object}
 */
function buildRoomWallGeometry(edgeStart, edgeEnd, wallHeight, floorY) {
  const br = { x: edgeStart.x, y: floorY, z: edgeStart.z };
  const bl = { x: edgeEnd.x, y: floorY, z: edgeEnd.z };
  const dx = edgeEnd.x - edgeStart.x;
  const dz = edgeEnd.z - edgeStart.z;
  const length = Math.hypot(dx, dz);
  // Inward normal is (dz, -dx) in z-down screen coords. With
  // normal = (sin(ry), cos(ry)), solve ry = atan2(dz, -dx).
  const rotationY = Math.atan2(dz, -dx);
  const topY = floorY + wallHeight;
  return {
    center: {
      x: (br.x + bl.x) / 2,
      y: floorY + wallHeight / 2,
      z: (br.z + bl.z) / 2,
    },
    dimensions: { x: length, y: wallHeight, z: 0 },
    rotation: { x: 0, y: rotationY, z: 0 },
    polygonCorners: [
      bl,
      br,
      { x: br.x, y: topY, z: br.z },
      { x: bl.x, y: topY, z: bl.z },
    ],
    alignment: { bottomLeft: bl, bottomRight: br },
  };
}

// --- Distance ---

/**
 * 3D Euclidean distance.
 * @param {Object} p1 - {x, y, z}
 * @param {Object} p2 - {x, y, z}
 * @returns {number}
 */
function calculateDistance3D(p1, p2) {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  const dz = p1.z - p2.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// --- rewrapRoomWall ---

/**
 * Project a point onto a surface axis, returning (along, perp) coordinates.
 * "along" is the signed distance along the axis from surfaceLeft to surfaceRight.
 * "perp" is the signed distance perpendicular to the axis.
 *
 * @param {Object} point - {x, y, z}
 * @param {Object} surfaceLeft - {x, y, z}
 * @param {Object} surfaceRight - {x, y, z}
 * @returns {Object} {along, perp, y}
 */
function projectOntoSurface(point, surfaceLeft, surfaceRight) {
  const axisX = surfaceRight.x - surfaceLeft.x;
  const axisZ = surfaceRight.z - surfaceLeft.z;
  const axisLength = Math.sqrt(axisX * axisX + axisZ * axisZ);

  if (axisLength < 0.0001) {
    return { along: 0, perp: 0, y: point.y };
  }

  // Unit direction along surface
  const dirX = axisX / axisLength;
  const dirZ = axisZ / axisLength;

  // Vector from surfaceLeft to point
  const relX = point.x - surfaceLeft.x;
  const relZ = point.z - surfaceLeft.z;

  const along = relX * dirX + relZ * dirZ;
  const perp = -relX * dirZ + relZ * dirX;

  return { along, perp, y: point.y };
}

/**
 * Reconstruct a world point from (along, perp, y) on a surface.
 *
 * @param {number} along - Distance along the surface axis
 * @param {number} perp - Perpendicular offset
 * @param {number} y - Y coordinate
 * @param {Object} surfaceLeft - {x, y, z}
 * @param {Object} surfaceRight - {x, y, z}
 * @returns {Object} {x, y, z}
 */
function unprojectFromSurface(along, perp, y, surfaceLeft, surfaceRight) {
  const axisX = surfaceRight.x - surfaceLeft.x;
  const axisZ = surfaceRight.z - surfaceLeft.z;
  const axisLength = Math.sqrt(axisX * axisX + axisZ * axisZ);

  if (axisLength < 0.0001) {
    return { x: surfaceLeft.x, y, z: surfaceLeft.z };
  }

  const dirX = axisX / axisLength;
  const dirZ = axisZ / axisLength;

  return {
    x: surfaceLeft.x + along * dirX - perp * dirZ,
    y,
    z: surfaceLeft.z + along * dirZ + perp * dirX,
  };
}

/**
 * Re-derive a room wall's geometry after its parent structure wall has moved/resized.
 *
 * The room wall's position on the structure wall surface is preserved proportionally:
 * - Determines which face of the structure wall the room wall sits on
 * - Projects old room wall corners onto the old structure surface → proportions
 * - Applies those proportions to the new structure surface
 * - Recomputes center, dimensions, rotation, start/end, and polygon corners
 *
 * @param {Object} roomWall - Room wall with alignment, center, dimensions, rotation, etc.
 * @param {Object} oldStructureWall - Old structure wall state {center, dimensions, rotation}
 * @param {Object} newStructureWall - New structure wall state {center, dimensions, rotation}
 * @returns {Object} Updated room wall with same id and alignment.structureWallId
 */
function rewrapRoomWall(roomWall, oldStructureWall, newStructureWall) {
  if (!roomWall.alignment) return roomWall;

  // 1. Determine which face the room wall is on
  const oldStructureCorners = calculateWallCorners(oldStructureWall);
  if (!oldStructureCorners) return roomWall;

  const facesSameDirection = calculateFacesSameDirection(
    oldStructureCorners,
    roomWall.alignment
  );

  // 2. Get old structure wall surface corners
  const oldSurface = calculateSurfaceCorners(
    oldStructureWall,
    facesSameDirection
  );
  if (!oldSurface) return roomWall;

  const oldSurfaceLength = calculateDistance3D(
    oldSurface.bottomLeft,
    oldSurface.bottomRight
  );
  if (oldSurfaceLength < 0.0001) return roomWall;

  // 3. Project room wall's alignment corners onto old surface → proportions
  const oldBL = roomWall.alignment.bottomLeft;
  const oldBR = roomWall.alignment.bottomRight;

  const projBL = projectOntoSurface(
    oldBL,
    oldSurface.bottomLeft,
    oldSurface.bottomRight
  );
  const projBR = projectOntoSurface(
    oldBR,
    oldSurface.bottomLeft,
    oldSurface.bottomRight
  );

  const tLeft = projBL.along / oldSurfaceLength;
  const tRight = projBR.along / oldSurfaceLength;

  // 4. Get new structure wall surface corners
  const newSurface = calculateSurfaceCorners(
    newStructureWall,
    facesSameDirection
  );
  if (!newSurface) return roomWall;

  const newSurfaceLength = calculateDistance3D(
    newSurface.bottomLeft,
    newSurface.bottomRight
  );

  // 5. Interpolate at tLeft/tRight on new surface → new bottomLeft/bottomRight
  const newBL = unprojectFromSurface(
    tLeft * newSurfaceLength,
    projBL.perp,
    oldBL.y,
    newSurface.bottomLeft,
    newSurface.bottomRight
  );

  const newBR = unprojectFromSurface(
    tRight * newSurfaceLength,
    projBR.perp,
    oldBR.y,
    newSurface.bottomLeft,
    newSurface.bottomRight
  );

  // 6. Derive dimensions, center, rotation, start/end
  const newWidth = calculateDistance3D(newBL, newBR);
  const newCenter = {
    x: (newBL.x + newBR.x) / 2,
    y: roomWall.center.y,
    z: (newBL.z + newBR.z) / 2,
  };

  const newDir = calculateDirectionFromPoints(newBL, newBR);
  const newRotationY = Math.atan2(-newDir.z, newDir.x);

  const halfHeight = roomWall.dimensions.y / 2;

  const updatedWall = {
    ...roomWall,
    center: newCenter,
    dimensions: {
      ...roomWall.dimensions,
      x: newWidth,
    },
    rotation: {
      ...roomWall.rotation,
      y: newRotationY,
    },
    start: {
      x: newCenter.x - (newWidth / 2) * newDir.x,
      y: newCenter.y - halfHeight,
      z: newCenter.z - (newWidth / 2) * newDir.z,
    },
    end: {
      x: newCenter.x + (newWidth / 2) * newDir.x,
      y: newCenter.y + halfHeight,
      z: newCenter.z + (newWidth / 2) * newDir.z,
    },
    alignment: {
      ...roomWall.alignment,
      bottomLeft: newBL,
      bottomRight: newBR,
      bottomMiddle: {
        x: (newBL.x + newBR.x) / 2,
        y: (newBL.y + newBR.y) / 2,
        z: (newBL.z + newBR.z) / 2,
      },
    },
  };

  // 7. Handle polygon corners: project each onto old surface, map to new surface
  if (
    roomWall.polygonCorners &&
    Array.isArray(roomWall.polygonCorners) &&
    roomWall.polygonCorners.length > 0
  ) {
    updatedWall.polygonCorners = roomWall.polygonCorners.map((corner) => {
      const proj = projectOntoSurface(
        corner,
        oldSurface.bottomLeft,
        oldSurface.bottomRight
      );
      const t = proj.along / oldSurfaceLength;
      return unprojectFromSurface(
        t * newSurfaceLength,
        proj.perp,
        corner.y || 0,
        newSurface.bottomLeft,
        newSurface.bottomRight
      );
    });
  }

  return updatedWall;
}

/**
 * Carry a wall's stored elevation outline onto new geometry.
 *
 * `polygonCorners` is the wall's ELEVATION OUTLINE in world coordinates — a
 * plain rectangle for most walls, a gable hexagon where Step 2.4 recovered a
 * profile. Nothing downstream re-derives it, and several readers PREFER it to
 * center/rotation (`getWallNormalFromPolygonCorners`, the opening-plane
 * derivation in the 3D viewer), so a wall whose centre or length moved while
 * its outline stayed put renders and hosts openings at its old position.
 *
 * Each corner keeps its FRACTION of the wall's half-length and its
 * perpendicular offset, so a rectangle re-emerges exactly and a recovered
 * profile keeps its shape. A profile stretches with the wall rather than
 * translating at one end — for the corner-joint moves that reach here (a
 * thickness or so on a wall metres long) that difference is a few
 * millimetres, against a whole-wall error if the outline is left behind.
 *
 * @param {Object} wall - the wall as stored (source of the outline)
 * @param {{center: Object, dimensions: Object, rotation: Object}} geometry
 * @returns {Array|null} new corners, or null when there is no outline to move
 */
function retargetWallPolygonCorners(wall, geometry) {
  const corners = wall?.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 3) return null;

  const oldLength = wall.dimensions?.x ?? 0;
  const newLength = geometry.dimensions?.x ?? 0;
  if (!(oldLength > EPSILON)) return null;

  const oldRot = wall.rotation?.y || 0;
  const newRot = geometry.rotation?.y ?? oldRot;
  const oldDir = { x: Math.cos(oldRot), z: -Math.sin(oldRot) };
  const newDir = { x: Math.cos(newRot), z: -Math.sin(newRot) };
  const oldPerp = { x: -oldDir.z, z: oldDir.x };
  const newPerp = { x: -newDir.z, z: newDir.x };
  const scale = newLength / oldLength;

  return corners.map((c) => {
    const relX = c.x - (wall.center?.x ?? 0);
    const relZ = c.z - (wall.center?.z ?? 0);
    const along = (relX * oldDir.x + relZ * oldDir.z) * scale;
    const perp = relX * oldPerp.x + relZ * oldPerp.z;
    return {
      x: geometry.center.x + along * newDir.x + perp * newPerp.x,
      y: c.y,
      z: geometry.center.z + along * newDir.z + perp * newPerp.z,
    };
  });
}

/**
 * Inward unit normal of a room wall in XZ — perpendicular to the wall
 * direction, pointing into the room.
 *
 * Derived directly from the CCW floor-polygon winding convention used
 * everywhere in the codebase: each room wall's `alignment.bottomLeft`
 * is `polygon[(i+1) % N]` and `alignment.bottomRight` is `polygon[i]`,
 * so the wall direction (BR → BL) is the polygon edge direction and
 * the polygon-CCW inward normal `(dz, -dx)` (rotation of the polygon
 * edge by 90° toward the interior) is `(-wall.dz, wall.dx)` in terms
 * of the wall's BL→BR direction.
 *
 * Prefer this over `getWallNormal`, which reads `wall.rotation` and so
 * gives a normal with no defined SIDE — a room wall is a zero-thickness
 * face and its two sides mean different rooms.
 *
 * The `roomCentroidXZ` argument is ignored — a previous version used
 * it to sign-flip a perpendicular candidate, but for L-shaped /
 * pentagon rooms the unweighted ceiling-vertex centroid can land
 * within a few cm of an edge (sometimes on the wrong side), flipping
 * the normal outward and pushing soffit insets past the floor
 * polygon. Kept in the signature for caller back-compat.
 */
// eslint-disable-next-line no-unused-vars
function wallInwardNormalXZ(wall, roomCentroidXZ) {
  const bl = wall.alignment.bottomLeft;
  const br = wall.alignment.bottomRight;
  const dx = br.x - bl.x;
  const dz = br.z - bl.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-9) return { x: 0, z: 0 };
  return { x: -dz / len, z: dx / len };
}

module.exports = {
  applyRotation,
  retargetWallPolygonCorners,
  getWallDirection,
  getWallNormal,
  wallInwardNormalXZ,
  calculateWallCorners,
  calculateDirectionFromPoints,
  calculateWallDirection,
  collinearUnionCoverage,
  COLLINEAR_PARALLEL_DOT_MIN,
  COLLINEAR_PERP_TOL_M,
  calculateFacesSameDirection,
  inputCapturedFromThisRoom,
  calculateInteriorOffset,
  calculateSurfaceCorners,
  findInteriorSurfaceCorners,
  buildRoomWallGeometry,
  calculateDistance3D,
  rewrapRoomWall,
  intersectLinesXZ,
  calculateWallFootprint,
  EPSILON,
  THIN_THRESHOLD,
  DEFAULT_THICKNESS,
  PARALLEL_THRESHOLD,
  ALIGNMENT_ANGLE_TOLERANCE,
};
